import { Check as CheckIcon, Flag as FlagIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { StyledFormControl, StyledMenuItem } from "@/components/ui";
import type { TestCase } from "./types";

interface BatchPriorityDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (priority: string) => void;
  selectedTests: TestCase[];
  isLoading: boolean;
}

export const BatchPriorityDialog: React.FC<BatchPriorityDialogProps> = ({
  open,
  onClose,
  onConfirm,
  selectedTests,
  isLoading,
}) => {
  const [selectedPriority, setSelectedPriority] = useState<string>("");

  const priorityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
  ];

  useEffect(() => {
    if (open) {
      setSelectedPriority("");
    }
  }, [open]);

  const handleConfirm = () => {
    if (selectedPriority) {
      onConfirm(selectedPriority);
    }
  };

  const handleClose = () => {
    setSelectedPriority("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
          border: "1px solid rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        },
      }}
      TransitionProps={{
        timeout: 200,
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <FlagIcon color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600 }}>
            Update Priority
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 0.875, pb: 0.875 }}>
        <Stack spacing={2.5}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                mb: 2,
                color: "text.primary",
              }}
            >
              Selected Tests ({selectedTests.length})
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary" }}>
              {selectedTests.slice(0, 5).map((test) => (
                <React.Fragment key={test.id}>
                  {test.name}
                  <br />
                </React.Fragment>
              ))}
              {selectedTests.length > 5 && (
                <Typography variant="caption" sx={{ fontSize: 11, fontStyle: "italic" }}>
                  ...and {selectedTests.length - 5} more
                </Typography>
              )}
            </Typography>
          </Paper>

          <StyledFormControl fullWidth size="small">
            <InputLabel>Priority</InputLabel>
            <Select
              label="Priority"
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
            >
              {priorityOptions.map((option) => (
                <StyledMenuItem key={option.value} value={option.value}>
                  <Typography variant="body2" fontWeight={500}>
                    {option.label}
                  </Typography>
                </StyledMenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 0.875, justifyContent: "flex-end" }}>
        <IconButton
          onClick={handleConfirm}
          disabled={!selectedPriority || isLoading}
          sx={{
            width: 40,
            height: 40,
            color: "grey.600",
            "&:hover": {
              bgcolor: "grey.300",
            },
            "&:disabled": {
              bgcolor: "grey.200",
              color: "grey.400",
            },
          }}
        >
          <CheckIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogActions>
    </Dialog>
  );
};
